import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Settings, Trash2, Bell, BellOff, Search, X, MapPin, Briefcase, Building, Clock, Calendar, Euro } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmModal from '../components/Common/ConfirmModal';
import SelectAllToggle from '../components/Common/SelectAllToggle';
import useConfirmDialog from '../hooks/useConfirmDialog';
import FilterModal from '../components/Jobs/FilterModal';
import WorkFieldCascadePicker, { WorkFieldSelection } from '../components/Common/WorkFieldCascadePicker';
import {
  getLocalSubscriptions,
  addLocalSubscription,
  updateLocalSubscription,
  deleteLocalSubscription,
  LocalSubscription,
} from '../services/localSavedSearches';
import { LITHUANIAN_CITIES, splitMajorCities } from '../constants/cities';
import './SavedSearches.scss';
import './Prenumeratos.scss';

interface SubscriptionItem {
  id: number | string;
  title: string;
  keywords?: string;
  location?: string[];
  work_field?: string[];
  field_ids?: number[];
  tipas_ids?: number[];
  position_ids?: number[];
  job_type?: string[];
  work_time?: string[];
  salary_min?: number;
  company_activity?: string;
  is_subscribed: boolean;
  notification_frequency?: 'immediate' | 'daily' | 'weekly';
  created_at: string;
}

const Prenumeratos: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogNode } = useConfirmDialog();
  const navigate = useNavigate();

  const [items, setItems] = useState<SubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<(number | string)[]>([]);
  const [titleFilter, setTitleFilter] = useState('');
  const [searchCounts, setSearchCounts] = useState<Record<string | number, { total: number; newCount: number }>>({});

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SubscriptionItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | string | null>(null);

  // Modal form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formFrequency, setFormFrequency] = useState<'' | 'immediate' | 'weekly'>('');
  const [formKeywords, setFormKeywords] = useState<string[]>([]);
  const [formLocation, setFormLocation] = useState<string[]>([]);
  const [formWorkFields, setFormWorkFields] = useState<WorkFieldSelection>({ fieldIds: [], tipasIds: [], positionIds: [] });
  const [formWorkFieldLabels, setFormWorkFieldLabels] = useState<string[]>([]);
  const [formJobType, setFormJobType] = useState<string[]>([]);
  const [formWorkTime, setFormWorkTime] = useState<string[]>([]);
  const [formSalaryMin, setFormSalaryMin] = useState('');
  const [formCompanyActivity, setFormCompanyActivity] = useState('');

  // CV templates from API
  interface CVTemplate { id: number; category: string; value: string; label: string; parent_value?: string; }
  const [cvTemplates, setCvTemplates] = useState<{
    work_field?: CVTemplate[];
    work_type?: CVTemplate[];
    work_time?: CVTemplate[];
    work_location?: CVTemplate[];
    company_activity?: CVTemplate[];
    position?: CVTemplate[];
    position_type?: CVTemplate[];
  }>({});
  const [companies, setCompanies] = useState<{ id: number; company_name: string; job_count: number; company_activity?: string }[]>([]);
  const [companySearch, setCompanySearch] = useState('');
  const [formCompanyNames, setFormCompanyNames] = useState<string[]>([]);

  // Search inputs for filter sections
  const [locationSearch, setLocationSearch] = useState('');
  const [salarySlider, setSalarySlider] = useState(0);

  // Taxonomy id → name maps, for rendering saved-search cards (Darbo sritys).
  const [taxFieldNames, setTaxFieldNames] = useState<Record<number, string>>({});
  const [taxTipasNames, setTaxTipasNames] = useState<Record<number, string>>({});

  const [activeFilterPanel, setActiveFilterPanel] = useState<string | null>(null);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const loadTemplates = async () => {
      try {
        const response = await fetch(`${API_URL}/cv-templates?grouped=true`);
        if (response.ok) {
          const result = await response.json();
          setCvTemplates(result.data || {});
        }
      } catch (error) {
        console.error('Error loading CV templates:', error);
      }
    };
    const loadCompanies = async () => {
      try {
        const response = await fetch(`${API_URL}/jobs/companies`);
        if (response.ok) {
          const result = await response.json();
          setCompanies(result.data?.companies || []);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
      }
    };
    const loadTaxonomy = async () => {
      try {
        const response = await fetch(`${API_URL}/taxonomy`);
        if (response.ok) {
          const result = await response.json();
          const fieldMap: Record<number, string> = {};
          (result.data?.darboSritys || []).forEach((f: any) => { fieldMap[f.id] = f.name; });
          const tipasMap: Record<number, string> = {};
          (result.data?.tipai || []).forEach((t: any) => { tipasMap[t.id] = t.name; });
          setTaxFieldNames(fieldMap);
          setTaxTipasNames(tipasMap);
        }
      } catch (error) {
        console.error('Error loading taxonomy:', error);
      }
    };
    loadTemplates();
    loadCompanies();
    loadTaxonomy();
  }, []);

  const mapSubToItem = (sub: LocalSubscription): SubscriptionItem => ({
    id: sub.id,
    title: sub.name,
    keywords: sub.keywords.join(', ') || undefined,
    location: sub.location,
    work_field: sub.work_field,
    field_ids: sub.field_ids,
    tipas_ids: sub.tipas_ids,
    position_ids: sub.position_ids,
    job_type: sub.job_type,
    work_time: sub.work_time,
    salary_min: sub.salary_min ?? undefined,
    is_subscribed: sub.enabled,
    notification_frequency: sub.frequency,
    created_at: sub.created_at,
  });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);

      if (!isAuthenticated) {
        const local = getLocalSubscriptions().map(mapSubToItem);
        setItems(local);
        return;
      }

      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/saved-searches/my`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        const result = await res.json();
        const all: SubscriptionItem[] = result.data || [];
        const subscribed = all.filter((s: SubscriptionItem) => s.is_subscribed);
        setItems(subscribed);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Build job-search params for a subscription. Darbo sritys use the canonical
  // taxonomy ids; legacy string work_field is a fallback for pre-taxonomy rows.
  const buildParams = (item: SubscriptionItem): URLSearchParams => {
    const params = new URLSearchParams();
    if (item.keywords) params.set('keywords', item.keywords);
    if (item.location?.length) params.set('location', item.location.join(','));
    if (item.field_ids?.length) params.set('fieldIds', item.field_ids.join(','));
    if (item.tipas_ids?.length) params.set('tipasIds', item.tipas_ids.join(','));
    if (item.position_ids?.length) params.set('positionIds', item.position_ids.join(','));
    if (!item.field_ids?.length && !item.tipas_ids?.length && !item.position_ids?.length && item.work_field?.length) {
      params.set('workField', item.work_field.join(','));
    }
    if (item.job_type?.length) params.set('jobType', item.job_type.join(','));
    if (item.work_time?.length) params.set('workTime', item.work_time.join(','));
    if (item.salary_min) params.set('salaryMin', item.salary_min.toString());
    if (item.company_activity) params.set('companyActivity', item.company_activity);
    return params;
  };

  const fetchCounts = useCallback(async (list: SubscriptionItem[]) => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const counts: Record<string | number, { total: number; newCount: number }> = {};

    const totalFrom = async (params: URLSearchParams): Promise<number> => {
      params.set('limit', '1');
      const res = await fetch(`${API_URL}/jobs/search/smart?${params.toString()}`);
      if (!res.ok) return 0;
      const data = await res.json();
      return data?.pagination?.total ?? 0;
    };

    await Promise.all(
      list.map(async (item) => {
        try {
          const params = buildParams(item);
          const paramsNew = new URLSearchParams(params);
          paramsNew.set('postedWithin', '3');

          const [total, newCount] = await Promise.all([
            totalFrom(new URLSearchParams(params)),
            totalFrom(paramsNew),
          ]);

          counts[item.id] = { total, newCount };
        } catch {
          counts[item.id] = { total: 0, newCount: 0 };
        }
      })
    );
    setSearchCounts(counts);
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    if (items.length > 0) fetchCounts(items);
  }, [items, fetchCounts]);

  const filtered = titleFilter.trim()
    ? items.filter((i) => i.title.toLowerCase().includes(titleFilter.toLowerCase().trim()))
    : items;

  const handleToggleSelectAll = () => {
    if (selectedIds.length === filtered.length && filtered.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((i) => i.id));
    }
  };

  const handleToggleSelect = (id: number | string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleDelete = (id: number | string) => {
    setDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (deleteId == null) return;

    if (!isAuthenticated) {
      deleteLocalSubscription(String(deleteId));
    } else {
      try {
        const token = localStorage.getItem('auth_token');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        await fetch(`${API_URL}/saved-searches/${deleteId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        console.error('Error deleting:', error);
        showToast('Nepavyko ištrinti', 'error');
      }
    }
    setShowDeleteConfirm(false);
    setDeleteId(null);
    fetchItems();
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!await confirm(`Ar tikrai norite ištrinti ${selectedIds.length} prenumeratų?`)) return;

    if (!isAuthenticated) {
      selectedIds.forEach((id) => deleteLocalSubscription(String(id)));
    } else {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      await Promise.all(
        selectedIds.map((id) =>
          fetch(`${API_URL}/saved-searches/${id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
    }
    setSelectedIds([]);
    fetchItems();
  };

  const handleToggleSubscription = async (id: number | string) => {
    if (!isAuthenticated) return;
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/saved-searches/${id}/toggle-subscription`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchItems();
    } catch (error) {
      console.error('Error toggling subscription:', error);
    }
  };


  const openCreateModal = () => {
    setEditingItem(null);
    setFormName('');
    setFormEmail('');
    setFormFrequency('');
    setFormKeywords([]);
    setFormLocation([]);
    setFormWorkFields({ fieldIds: [], tipasIds: [], positionIds: [] });
    setFormWorkFieldLabels([]);
    setFormJobType([]);
    setFormWorkTime([]);
    setFormSalaryMin('');
    setFormCompanyActivity('');
    setLocationSearch('');
    setShowModal(true);
  };

  const openEditModal = (item: SubscriptionItem) => {
    setEditingItem(item);
    setFormName(item.title);
    setFormFrequency(
      item.notification_frequency === 'immediate' || item.notification_frequency === 'weekly'
        ? item.notification_frequency
        : ''
    );
    setFormKeywords(item.keywords ? item.keywords.split(',').map((k) => k.trim()).filter(Boolean) : []);
    setFormLocation(item.location || []);
    setFormWorkFields({
      fieldIds: item.field_ids || [],
      tipasIds: item.tipas_ids || [],
      positionIds: item.position_ids || [],
    });
    setFormWorkFieldLabels([]);
    setFormJobType(item.job_type || []);
    setFormWorkTime(item.work_time || []);
    setFormSalaryMin(item.salary_min?.toString() || '');
    setFormCompanyActivity(item.company_activity || '');
    setLocationSearch('');
    setShowModal(true);
  };

  const toggleArray = (arr: string[], val: string): string[] =>
    arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

  const workFieldCount = formWorkFields.fieldIds.length + formWorkFields.tipasIds.length + formWorkFields.positionIds.length;

  const handleSave = async () => {
    if (!formName.trim()) {
      showToast('Prašome įvesti pavadinimą', 'error');
      return;
    }
    if (!formFrequency) {
      showToast('Prašome pasirinkti skelbimo siuntimo dažnį', 'error');
      return;
    }

    if (!isAuthenticated) {
      const subData = {
        name: formName,
        email: formEmail.trim(),
        frequency: formFrequency,
        keywords: formKeywords,
        location: formLocation,
        work_field: [],
        field_ids: formWorkFields.fieldIds,
        tipas_ids: formWorkFields.tipasIds,
        position_ids: formWorkFields.positionIds,
        job_type: formJobType,
        work_time: formWorkTime,
        salary_min: formSalaryMin ? parseFloat(formSalaryMin) : null,
        enabled: true,
      };

      if (editingItem) {
        updateLocalSubscription(String(editingItem.id), subData);
      } else {
        addLocalSubscription(subData);

        if (formEmail.trim()) {
          // Also send to backend for actual email delivery
          try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            await fetch(`${API_URL}/subscriptions/guest`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: formEmail.trim(),
                name: formName,
                filters: {
                  keywords: formKeywords,
                  location: formLocation,
                  field_ids: formWorkFields.fieldIds,
                  tipas_ids: formWorkFields.tipasIds,
                  position_ids: formWorkFields.positionIds,
                  job_type: formJobType,
                  work_time: formWorkTime,
                  salary_min: formSalaryMin ? parseFloat(formSalaryMin) : null,
                },
                frequency: formFrequency,
              }),
            });
          } catch { /* fire-and-forget */ }
        }
      }
      showToast(editingItem ? 'Prenumerata atnaujinta!' : 'Prenumerata sukurta!', 'success');
      setShowModal(false);
      fetchItems();
      return;
    }

    // Authenticated path
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const payload = {
        title: formName,
        keywords: formKeywords.length > 0 ? formKeywords.join(', ') : null,
        location: formLocation.length > 0 ? formLocation : null,
        work_field: null,
        field_ids: formWorkFields.fieldIds.length > 0 ? formWorkFields.fieldIds : null,
        tipas_ids: formWorkFields.tipasIds.length > 0 ? formWorkFields.tipasIds : null,
        position_ids: formWorkFields.positionIds.length > 0 ? formWorkFields.positionIds : null,
        job_type: formJobType.length > 0 ? formJobType : null,
        work_time: formWorkTime.length > 0 ? formWorkTime : null,
        salary_min: formSalaryMin ? parseFloat(formSalaryMin) : null,
        company_activity: formCompanyActivity || null,
        is_subscribed: true,
        notification_frequency: formFrequency,
      };

      const url = editingItem
        ? `${API_URL}/saved-searches/${editingItem.id}`
        : `${API_URL}/saved-searches`;

      const res = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        showToast(editingItem ? 'Prenumerata atnaujinta!' : 'Prenumerata sukurta!', 'success');
        setShowModal(false);
        fetchItems();
      } else {
        showToast('Nepavyko išsaugoti', 'error');
      }
    } catch (error) {
      console.error('Error saving subscription:', error);
      showToast('Įvyko klaida', 'error');
    }
  };

  const formatCriteria = (item: SubscriptionItem): string => {
    const parts: string[] = [];
    if (item.keywords) parts.push(item.keywords);
    if (item.location?.length) parts.push(item.location.join(', '));
    const fieldLabels = (item.field_ids || []).map((id) => taxFieldNames[id]).filter(Boolean) as string[];
    const tipasLabels = (item.tipas_ids || []).map((id) => taxTipasNames[id]).filter(Boolean) as string[];
    if (fieldLabels.length) parts.push(fieldLabels.join(', '));
    if (tipasLabels.length) parts.push(tipasLabels.join(', '));
    if (item.position_ids?.length) parts.push(`${item.position_ids.length} pareigybės`);
    if (!fieldLabels.length && !tipasLabels.length && !item.position_ids?.length && item.work_field?.length) {
      parts.push(item.work_field.join(', '));
    }
    if (item.job_type?.length) parts.push(item.job_type.join(', '));
    if (item.work_time?.length) parts.push(item.work_time.join(', '));
    if (item.salary_min) parts.push(`Nuo ${item.salary_min}€`);
    return parts.length > 0 ? parts.join(', ') : 'Visi skelbimai';
  };

  return (
    <div className="saved-searches-page prenumeratos-page">
      {ConfirmDialogNode}
      <div className="container">
        <div className="header-row">
          <div className="breadcrumbs">
            <span onClick={() => navigate('/')}>Pradžia</span>
            <span className="separator">&gt;</span>
            <span className="current">Prenumeratos</span>
          </div>
          <div className="filters-toolbar">
            <SelectAllToggle
              selectedCount={selectedIds.length}
              totalCount={filtered.length}
              onToggle={handleToggleSelectAll}
            />
            <button
              className="btn-delete"
              onClick={handleDeleteSelected}
              disabled={selectedIds.length === 0}
            >
              <Trash2 size={16} />
              <span>Trinti</span>
            </button>
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-title-input"
                placeholder="Paieškos pavadinimas"
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
              />
              <Search size={18} className="search-icon" />
            </div>
            <button className="btn-new-search" onClick={openCreateModal}>
              <Plus size={18} />
              <span>Nauja prenumerata</span>
            </button>
          </div>
        </div>

        <div className="searches-section">
          {loading ? (
            <div className="loading">Kraunama...</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <Search size={48} />
              <p>{titleFilter ? 'Prenumeratų pagal šį filtrą nerasta' : 'Dar neturite prenumeratų'}</p>
              {!titleFilter && (
                <button className="btn-primary" onClick={openCreateModal}>
                  Sukurti pirmą prenumeratą
                </button>
              )}
            </div>
          ) : (
            <div className="searches-grid">
              {filtered.map((item) => (
                <div
                  key={item.id}
                  className={`search-card ${selectedIds.includes(item.id) ? 'selected' : ''}`}
                >
                  <div className="search-card-header">
                    <div className="header-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => handleToggleSelect(item.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {isAuthenticated && (
                        <button
                          className={`subscription-label ${item.is_subscribed ? 'active' : ''}`}
                          onClick={() => handleToggleSubscription(item.id)}
                        >
                          {item.is_subscribed ? <Bell size={14} /> : <BellOff size={14} />}
                          <span>{item.is_subscribed ? 'Prenumeruojama' : 'Prenumeruoti'}</span>
                        </button>
                      )}
                    </div>
                    <div className="header-actions">
                      <button
                        className="btn-icon"
                        onClick={() => openEditModal(item)}
                        title="Nustatymai"
                      >
                        <Settings size={16} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDelete(item.id)}
                        title="Ištrinti"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="search-card-body" onClick={() => openEditModal(item)}>
                    <h3 className="search-title">{item.title}</h3>
                    <div className="search-criteria">
                      <span className="criteria-label">Kriterijai:</span>
                      <p className="criteria-text">{formatCriteria(item)}</p>
                    </div>
                  </div>

                  <div className="search-card-footer">
                    <span className="result-count">
                      Iš viso: <strong>{searchCounts[item.id]?.total ?? '...'}</strong>
                    </span>
                    <span className="divider" />
                    <span className="new-count">
                      Naujų: <strong>{searchCounts[item.id]?.newCount ?? '...'}</strong>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal — two-panel layout */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content pren-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Redaguoti prenumeratą' : 'Nauja prenumerata'}</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="pren-modal-body">
              {/* Name field */}
              <div className="pren-name-row">
                <div className="form-group">
                  <label>Paieškos pavadinimas *</label>
                  <input type="text" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="pvz., Programuotojas Vilniuje" />
                </div>
              </div>

              {/* Frequency selector */}
              <div className="pren-frequency-row">
                <span className="pren-frequency-label">Skelbimo siuntimo dažnis *</span>
                <div className="pren-frequency-options">
                  {([
                    { value: 'immediate', label: 'Nedelsiant, atsiradus naujam skelbimui' },
                    { value: 'weekly',    label: 'Kas savaitę' },
                  ] as const).map(opt => (
                    <label key={opt.value} className="pren-frequency-option">
                      <input
                        type="radio"
                        name="pren-frequency"
                        checked={formFrequency === opt.value}
                        onChange={() => setFormFrequency(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Filter cards grid */}
              <div className="pren-filter-cards">
                {[
                  { key: 'location',        icon: <MapPin size={24} />,    label: 'Darbo vietos',   count: formLocation.length,           chips: formLocation },
                  { key: 'workField',       icon: <Briefcase size={24} />, label: 'Darbo sritys',   count: workFieldCount,                chips: formWorkFieldLabels },
                  { key: 'companyActivity', icon: <Building size={24} />,  label: 'Įmonės',         count: formCompanyActivity ? 1 : 0,   chips: formCompanyActivity ? [formCompanyActivity] : [] },
                  { key: 'jobType',         icon: <Clock size={24} />,     label: 'Darbo tipas',    count: formJobType.length,            chips: formJobType },
                  { key: 'workTime',        icon: <Calendar size={24} />,  label: 'Darbo laikas',   count: formWorkTime.length,           chips: formWorkTime },
                  { key: 'salary',          icon: <Euro size={24} />,      label: 'Atlyginimas',    count: formSalaryMin ? 1 : 0,         chips: formSalaryMin ? [`Nuo ${formSalaryMin} €`] : [] },
                ].map(({ key, icon, label, count, chips }) => (
                  <div key={key} className="pren-filter-card" onClick={() => setActiveFilterPanel(key)}>
                    <div className="pren-card-header">
                      <span className="pren-card-icon-wrap">
                        {icon}
                        {count > 0 && <span className="pren-filter-badge">{count}</span>}
                      </span>
                      <span className="pren-card-label">{label}</span>
                    </div>
                    {chips.length > 0 ? (
                      <div className="pren-card-chips">
                        {chips.map(v => <span key={v} className="pren-selected-chip">{v}</span>)}
                      </div>
                    ) : (
                      <button type="button" className="pren-card-select-btn">
                        <span>+</span> PASIRINKTI
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Atšaukti</button>
              <button className="btn-primary" onClick={handleSave} disabled={!formName.trim() || !formFrequency}>{editingItem ? 'Atnaujinti' : 'Išsaugoti'}</button>
            </div>
          </div>
        </div>
      )}

      {/* DARBO VIETOS */}
      <FilterModal
        isOpen={activeFilterPanel === 'location'}
        onClose={() => setActiveFilterPanel(null)}
        title="Darbo vietos"
        icon={<MapPin size={20} />}
        onShowResults={() => setActiveFilterPanel(null)}
        actionLabel="Išsaugoti"
      >
        <input type="text" placeholder="Įveskite miestą" value={locationSearch} onChange={(e) => setLocationSearch(e.target.value)} className="filter-input" />
        {(() => {
          const all = (cvTemplates.work_location || LITHUANIAN_CITIES.map((c, i) => ({ id: i, category: 'work_location', value: c, label: c }))).filter(l => !locationSearch || l.value.toLowerCase().includes(locationSearch.toLowerCase()));
          const { major: top, rest } = splitMajorCities(all);
          const renderLoc = (loc: any) => (
            <label key={loc.value} className="city-checkbox-label">
              <input type="checkbox" checked={formLocation.includes(loc.value)} onChange={() => setFormLocation(prev => toggleArray(prev, loc.value))} />
              <span className="filter-option-label">{loc.value}</span>
            </label>
          );
          return (<>{top.length > 0 && <div className="city-top5">{top.map(renderLoc)}</div>}{top.length > 0 && rest.length > 0 && <hr className="city-top5-divider" />}{rest.length > 0 && <div className="cities-grid">{rest.map(renderLoc)}</div>}</>);
        })()}
      </FilterModal>

      {/* DARBO SRITYS */}
      <FilterModal
        isOpen={activeFilterPanel === 'workField'}
        onClose={() => setActiveFilterPanel(null)}
        title="Darbo sritys"
        icon={<Briefcase size={20} />}
        onShowResults={() => setActiveFilterPanel(null)}
        actionLabel="Išsaugoti"
      >
        <WorkFieldCascadePicker
          value={formWorkFields}
          onChange={setFormWorkFields}
          onLabelsChange={setFormWorkFieldLabels}
        />
      </FilterModal>

      {/* ĮMONĖS */}
      <FilterModal
        isOpen={activeFilterPanel === 'companyActivity'}
        onClose={() => setActiveFilterPanel(null)}
        title="Įmonės"
        icon={<Building size={20} />}
        onShowResults={() => setActiveFilterPanel(null)}
        actionLabel="Išsaugoti"
      >
        <div className="two-column-layout">
          <div className="filter-column">
            <h4>Įmonių veiklos sritys</h4>
            <div className="checkbox-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {(() => {
                const sectorMap = new Map<string, number>();
                companies.forEach(c => {
                  if (c.company_activity?.trim()) sectorMap.set(c.company_activity, (sectorMap.get(c.company_activity) || 0) + c.job_count);
                });
                return Array.from(sectorMap.entries()).sort((a, b) => b[1] - a[1]).map(([sector]) => (
                  <label key={sector} className="checkbox-label">
                    <input type="radio" name="pren-companyActivity" checked={formCompanyActivity === sector} onChange={() => setFormCompanyActivity(formCompanyActivity === sector ? '' : sector)} />
                    <span className="filter-option-label">{sector}</span>
                  </label>
                ));
              })()}
            </div>
          </div>
          <div className="filter-column">
            <h4>Įmonės</h4>
            <input type="text" placeholder="Įveskite įmonės pavadinimą" value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} className="filter-input" />
            <div className="checkbox-list" style={{ maxHeight: '360px', overflowY: 'auto' }}>
              {companies.filter(c => {
                if (formCompanyActivity && c.company_activity !== formCompanyActivity) return false;
                if (companySearch && !c.company_name.toLowerCase().includes(companySearch.toLowerCase())) return false;
                return true;
              }).map(c => (
                <label key={c.company_name} className="checkbox-label">
                  <input type="checkbox" checked={formCompanyNames.includes(c.company_name)} onChange={() => setFormCompanyNames(prev => toggleArray(prev, c.company_name))} />
                  <span>{c.company_name} ({c.job_count})</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </FilterModal>

      {/* DARBO TIPAS */}
      <FilterModal
        isOpen={activeFilterPanel === 'jobType'}
        onClose={() => setActiveFilterPanel(null)}
        title="Darbo tipas"
        icon={<Clock size={20} />}
        onShowResults={() => setActiveFilterPanel(null)}
        actionLabel="Išsaugoti"
      >
        <div className="checkbox-list single-column">
          {(cvTemplates.work_type || []).map(type => (
            <label key={type.id} className="checkbox-label">
              <input type="checkbox" checked={formJobType.includes(type.value)} onChange={() => setFormJobType(prev => toggleArray(prev, type.value))} />
              <span className="filter-option-label">{type.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* DARBO LAIKAS */}
      <FilterModal
        isOpen={activeFilterPanel === 'workTime'}
        onClose={() => setActiveFilterPanel(null)}
        title="Darbo laikas"
        icon={<Calendar size={20} />}
        onShowResults={() => setActiveFilterPanel(null)}
        actionLabel="Išsaugoti"
      >
        <div className="checkbox-list single-column">
          {(cvTemplates.work_time || []).map(time => (
            <label key={time.id} className="checkbox-label">
              <input type="checkbox" checked={formWorkTime.includes(time.value)} onChange={() => setFormWorkTime(prev => toggleArray(prev, time.value))} />
              <span className="filter-option-label">{time.value}</span>
            </label>
          ))}
        </div>
      </FilterModal>

      {/* ATLYGINIMAS */}
      <FilterModal
        isOpen={activeFilterPanel === 'salary'}
        onClose={() => setActiveFilterPanel(null)}
        title="Atlyginimas"
        icon={<Euro size={20} />}
        onShowResults={() => setActiveFilterPanel(null)}
        actionLabel="Išsaugoti"
      >
        <div className="salary-slider-wrap">
          <div className="salary-slider-label">
            {salarySlider === 0 ? 'Bet koks atlyginimas' : `Nuo ${salarySlider.toLocaleString('lt-LT')} €`}
          </div>
          <input
            type="range"
            min={0}
            max={10000}
            step={50}
            value={salarySlider}
            style={{ '--slider-pct': `${(salarySlider / 10000) * 100}%` } as React.CSSProperties}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              setSalarySlider(val);
              setFormSalaryMin(val === 0 ? '' : String(val));
            }}
          />
          <div className="salary-slider-range">
            <span>0 €</span>
            <span>10 000 €</span>
          </div>
        </div>
      </FilterModal>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        message="Ar tikrai norite ištrinti šią prenumeratą? Šio veiksmo negalima atšaukti."
        confirmText="OK"
        cancelText="Atšaukti"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteId(null);
        }}
      />
    </div>
  );
};

export default Prenumeratos;
